<script setup>
import { computed, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { PlusSignCircleIcon, Search01Icon } from '@hugeicons/core-free-icons';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
  plusButtonClass: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['select', 'open', 'close']);

const isOpen = ref(false);
const searchQuery = ref('');
const showSearchInput = ref(false);

const filteredItems = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return props.items;

  return props.items.filter(
    item =>
      item.title.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query)
  );
});

const close = () => {
  if (!isOpen.value) return;
  isOpen.value = false;
  searchQuery.value = '';
  showSearchInput.value = false;
  emit('close');
};

const toggle = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    emit('open');
  } else {
    searchQuery.value = '';
    showSearchInput.value = false;
    emit('close');
  }
};

const toggleSearch = () => {
  showSearchInput.value = !showSearchInput.value;
  if (!showSearchInput.value) {
    searchQuery.value = '';
  }
};

const onSelect = actionKey => {
  emit('select', actionKey);
  close();
};

const PLUS_ICON_SIZE = 22;
const STEP_ICON_SIZE = 22;
</script>

<template>
  <div
    v-on-click-outside="close"
    class="relative z-20 flex shrink-0 flex-col items-center"
  >
    <button
      type="button"
      class="relative z-20 flex size-[22px] items-center justify-center rounded-full border-0 bg-transparent p-0 shadow-none outline-none transition-opacity hover:opacity-80"
      :class="plusButtonClass"
      :title="$t('AUTOMATION.ADD.ACTION_BUTTON_LABEL')"
      :aria-label="$t('AUTOMATION.ADD.ACTION_BUTTON_LABEL')"
      :aria-expanded="isOpen"
      @click.stop="toggle"
    >
      <HugeIcon
        :icon="PlusSignCircleIcon"
        :size="PLUS_ICON_SIZE"
        class="pointer-events-none shrink-0"
      />
    </button>

    <div
      v-if="isOpen"
      class="absolute left-1/2 top-full z-50 mt-2 flex w-[22rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-lg dark:border-n-strong dark:bg-n-solid-2"
      role="menu"
      @click.stop
    >
      <div
        class="flex items-center justify-between gap-2 border-b border-n-weak px-3 py-2.5 dark:border-n-strong"
      >
        <span class="text-sm font-semibold text-n-slate-12">
          {{ $t('AUTOMATION.FLOW.ADD_STEPS.TITLE') }}
        </span>
        <button
          type="button"
          class="flex size-7 shrink-0 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
          :aria-label="$t('AUTOMATION.FLOW.ADD_STEPS.SEARCH_PLACEHOLDER')"
          @click="toggleSearch"
        >
          <HugeIcon
            :icon="Search01Icon"
            :size="18"
            class="pointer-events-none shrink-0"
          />
        </button>
      </div>

      <div
        v-if="showSearchInput"
        class="border-b border-n-weak px-3 py-2 dark:border-n-strong"
      >
        <input
          v-model="searchQuery"
          type="search"
          class="h-9 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand dark:border-n-strong dark:bg-n-solid-3"
          :placeholder="$t('AUTOMATION.FLOW.ADD_STEPS.SEARCH_PLACEHOLDER')"
        />
      </div>

      <ul class="m-0 max-h-80 list-none overflow-y-auto p-1">
        <li
          v-if="!filteredItems.length"
          class="px-3 py-4 text-center text-sm text-n-slate-10"
        >
          {{ $t('DROPDOWN_MENU.EMPTY_STATE') }}
        </li>
        <li v-for="item in filteredItems" :key="item.key" role="none">
          <button
            type="button"
            role="menuitem"
            class="flex w-full gap-2.5 rounded-lg px-2 py-2.5 text-start transition-colors hover:bg-n-alpha-2 dark:hover:bg-n-alpha-3"
            @click="onSelect(item.key)"
          >
            <HugeIcon
              :icon="item.icon"
              :size="STEP_ICON_SIZE"
              class="pointer-events-none mt-0.5 shrink-0"
              :class="item.iconClass"
            />
            <span class="flex min-w-0 flex-1 flex-col gap-0.5">
              <span class="text-sm font-medium text-n-slate-12">{{
                item.title
              }}</span>
              <span class="line-clamp-2 text-xs leading-snug text-n-slate-10">
                {{ item.description }}
              </span>
            </span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>
